import React, { createContext, useContext, useReducer } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useQueryString } from '../hooks';

/**
 * @internal
 * @template State
 */

/**
 * creates a new filter context and provider
 */
export const filterProviderFactory = () => {
	const Context = createContext(
		/**˚
		 * @internal please leave undefined in parenthesis until this has been migrated to typescript.
		 * This is so that JSDOC can cast the type for the param since we can't provide a type argument directly
		 * to create context
		 * the initial context
		 * @type {FilterProviderFactory.Context<State> | undefined}
		 */
		(undefined)
	);
	const Provider = createProvider(Context);

	const useFilters = () => {
		const context = useContext(Context);
		if (context === undefined) {
			throw new Error('useFilter must be used within a FilterProvider');
		}

		return context;
	};

	return {
		Context,
		Provider,
		useFilters,
	};
};

/**
 * creates an enhanced provider from the default context
 * @param {React.Context<FilterProviderFactory.Context<State> | undefined>} Context
 * @returns {React.FC<{ mapControlsToState?: FilterProviderFactory.MapFn<State>}>}
 */
const createProvider = (Context) => ({ children, mapControlsToState }) => {
	/**
	 * we expect the query string to include part of the expected state
	 * @type {{ query: Partial<State>, qs: any }}
	 */
	const { query: state, qs } = useQueryString();
	const { pathname } = useLocation();
	const history = useHistory();

	/**
	 * update the the managed state in the query, ignoring all irrelevant params
	 * @param {Partial<State>} values the incoming values to update
	 */
	const update = (values) => {
		const merged = { ...state, ...values };
		const mapped =
			typeof mapControlsToState === 'function'
				? mapControlsToState(merged)
				: merged;
		const cleaned = removeEmptyValues(mapped);
		const stringified = qs.stringify(cleaned);
		const url = `${pathname}${stringified.length ? `?${stringified}` : ''}`;
		history.push(url);
	};

	/**
	 * clear the managed state ignoring all irrelevant params
	 * @param {FilterProviderFactory.Exceptions<State>} exceptions values in the managed state to ignore when clearing
	 */
	const clear = (...exceptions) => {
		if (!state) return;

		/**
		 * this is the state that this filter provider manages
		 */
		const managedState =
			typeof mapControlsToState === 'function'
				? mapControlsToState(state)
				: { ...state };

		/**
		 * remove a key from the params if it is managed AND if it is NOT in the exceptions
		 * @param {string} key
		 * @returns {boolean} true if it should be removed, false otherwise
		 */
		const checkToRemove = (key) =>
			managedState[key] && exceptions.indexOf(key) === -1;

		/**
		 * create an object of the unmanaged state
		 */
		const other = Object.keys(state).reduce(
			(unmanaged, key) =>
				checkToRemove(key)
					? {
							...unmanaged,
					  }
					: { [key]: state[key], ...unmanaged },
			{}
		);

		const stringified = qs.stringify(other);
		const url = `${pathname}${stringified.length ? `?${stringified}` : ''}`;

		history.push(url);
	};

	/**
	 * the value that will be accessible from useContext
	 * @type {FilterProviderFactory.Context<State>}
	 */
	const value = { state, update, clear };

	return <Context.Provider value={value}>{children}</Context.Provider>;
};

/**
 * Removes empty values so that url string is clean
 * @param {*} values
 * @returns {Partial<State>}
 */
function removeEmptyValues(values) {
	const cleaned = Object.keys(values).reduce((vals, key) => {
		if (values[key] === undefined || values[key] === null || values[key] === '')
			return { ...vals };
		return { ...vals, [key]: values[key] };
	}, {});
	return cleaned;
}
